/**
 * ELK-based flowchart layout (async).
 * Produces node positions and orthogonal edge paths in one pass.
 *
 * Sync callers (persistence, nest helpers, etc.) continue to use `calculateLayout` in layout/index.js.
 */

import { ensureElk } from '../elkSingleton';
import { buildDependencyGraph, findDisconnectedComponents } from '../index';
import { withLayoutDimensionDefaults } from '../layoutDimensions';
import { applyStoredRootOrder } from '../topLevelOrigins';
import {
  refreshChainInfo,
  snapshotPositions,
  spaceSeparateWorkflows,
  warpEdgesToMovedNodes,
} from './elkGeometry';
import { assembleComponentLayouts } from './placeElkChains';

function orderComponents(components, chainOrder) {
  const orderedComponents = [...components];
  if (!chainOrder || chainOrder.length === 0) return orderedComponents;

  const orderMap = new Map(chainOrder.map((id, idx) => [id, idx]));
  orderedComponents.sort((a, b) => {
    const orderA = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
    const orderB = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
    if (orderA !== orderB) return orderA - orderB;
    return (a.id || '').localeCompare(b.id || '');
  });
  return orderedComponents;
}

function finishElkLayout(laid, tasks, nodeDimensions, chainOrder) {
  const { positions, edgePaths, links, chainInfo } = laid;
  const before = snapshotPositions(positions);
  applyStoredRootOrder(positions, tasks, nodeDimensions, chainOrder);
  warpEdgesToMovedNodes(edgePaths, links, before, positions);
  // Each connection-separate workflow keeps the ELK layout it was given.
  // Slide a later one right when its cards or connectors would touch an earlier one.
  spaceSeparateWorkflows(positions, edgePaths, links, tasks, nodeDimensions);
  refreshChainInfo(chainInfo, positions, nodeDimensions);
  return { positions, chainInfo, edgePaths };
}

/**
 * @param {Array} tasks
 * @param {Object} nodeDimensions taskId -> { width, height }
 * @param {Array<string>|null} chainOrder
 * @returns {Promise<{ positions: Object, chainInfo: Array, edgePaths: Record<string, string> }>}
 */
export async function calculateLayoutElk(
  tasks,
  nodeDimensions = {},
  chainOrder = null,
) {
  if (!tasks || tasks.length === 0) {
    return { positions: {}, chainInfo: [], edgePaths: {} };
  }

  const dims = withLayoutDimensionDefaults(tasks, nodeDimensions);
  const elk = await ensureElk();
  const graph = buildDependencyGraph(tasks);
  const orderedComponents = orderComponents(findDisconnectedComponents(tasks, graph), chainOrder);
  const laid = await assembleComponentLayouts(elk, orderedComponents, dims);
  return finishElkLayout(laid, tasks, dims, chainOrder);
}
